import React, { useMemo, useState } from 'react';
import { useStatusPalette } from '../../map/hooks/useStatusPalette';
import { useEngagementGroups, newGroupId, GROUP_COLORS, type EngagementGroup } from '../../map/hooks/useEngagementGroups';
import type { Vehicule } from '../../../../shared/types/game';

/**
 * Tiroir « Réglages — couleurs et groupes » — port fidèle du prototype
 * (`settings.js` drawerReglages) : grille de pastilles de statuts (fond + texte,
 * réinitialisables) et liste éditable des groupes d'engagement (composition de
 * départ type). Branché sur useStatusPalette (persisté serveur) et
 * useEngagementGroups.
 */

interface Props {
  playerVehicles: Vehicule[];
}

const ReglagesDrawer: React.FC<Props> = ({ playerVehicles }) => {
  const { palette, setColor, resetAll, isCustomised } = useStatusPalette();
  const { groups, saveGroup, deleteGroup } = useEngagementGroups();

  const cles = Object.keys(palette);
  const modifiees = cles.filter((k) => isCustomised(k)).length;

  const fleetTypes = useMemo(
    () => Array.from(new Set((playerVehicles || []).map((v) => v.type || 'Type inconnu'))).sort(),
    [playerVehicles],
  );

  // Ligne « nouveau groupe ».
  const [ngNom, setNgNom] = useState('');
  const [ngCouleur, setNgCouleur] = useState(GROUP_COLORS[1] || '#38bdf8');
  const [ngCompo, setNgCompo] = useState<Record<string, number>>({});

  const total = (compo: Record<string, number>) => Object.values(compo).reduce((n, v) => n + (Number(v) || 0), 0);

  const patchGroup = (g: EngagementGroup, patch: Partial<EngagementGroup>) => saveGroup({ ...g, ...patch });
  const patchCompo = (g: EngagementGroup, type: string, val: number) =>
    saveGroup({ ...g, compo: { ...g.compo, [type]: Math.max(0, val) } });

  const createGroup = () => {
    const compo: Record<string, number> = {};
    Object.entries(ngCompo).forEach(([t, n]) => {
      if (Number(n) > 0) compo[t] = Number(n);
    });
    if (!Object.keys(compo).length) return;
    saveGroup({ id: newGroupId(), nom: ngNom.trim() || 'Groupe', couleur: ngCouleur, compo });
    setNgNom('');
    setNgCompo({});
  };

  return (
    <>
      <p className="dintro">
        Deux réglages qui appartiennent au joueur, pas au jeu : les couleurs qu'il lit toute la
        journée, et les compositions de départ qu'il utilise en boucle. Les deux sont sauvegardés
        sur son compte.
      </p>

      <div className="block">
        <div className="block__h">
          Couleurs des statuts{' '}
          <span className="mono muted">
            {cles.length} statuts{modifiees ? ` · ${modifiees} modifié(s)` : ' · valeurs du jeu'}
          </span>
        </div>
        <div className="block__b">
          <p className="bsub">
            Le fond <b>et</b> l'écriture comptent : certains statuts partagent la même couleur de fond
            et ne se distinguent que par la couleur du texte. Ajustez fond et texte de chaque statut.
          </p>

          <div className="palgrid">
            {cles.map((k) => {
              const c = palette[k];
              const perso = isCustomised(k);
              return (
                <div className="palcell" key={k}>
                  <div className="palcell__apercu" style={{ background: c.bg, color: c.fg }}>{c.label}</div>
                  <div className="palcell__ctl">
                    <label title="Couleur de fond">
                      <span>Fond</span>
                      <input type="color" value={c.bg} onChange={(e) => setColor(k, 'bg', e.target.value)} />
                    </label>
                    <label title="Couleur d'écriture">
                      <span>Texte</span>
                      <input type="color" value={c.fg} onChange={(e) => setColor(k, 'fg', e.target.value)} />
                    </label>
                    {perso ? (
                      <button
                        className="btn btn--sm btn--ghost"
                        type="button"
                        title="Revenir au défaut"
                        onClick={() => {
                          setColor(k, 'bg', null);
                          setColor(k, 'fg', null);
                        }}
                      >
                        ↺
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            className="btn btn--sm btn--danger"
            style={{ marginTop: 9 }}
            type="button"
            disabled={!modifiees}
            onClick={() => resetAll()}
          >
            Rétablir toutes les couleurs du jeu
          </button>
        </div>
      </div>

      <div className="block">
        <div className="block__h">
          Groupes d'engagement <span className="mono muted">{groups.length} groupe(s)</span>
        </div>
        <div className="block__b">
          <p className="bsub">
            Un groupe, c'est une composition de départ que vous réutilisez : un clic sélectionne d'un
            coup les engins correspondants, disponibles et armés. Vous validez ensuite avec
            <b> Engager</b>. Les boutons apparaissent au-dessus du moniteur d'engins.
          </p>

          <div className="grplist">
            {groups.length ? (
              groups.map((g) => (
                <div className="grpline" style={{ '--c': g.couleur } as React.CSSProperties} key={g.id}>
                  <input
                    type="color"
                    value={g.couleur}
                    title="Couleur du bouton"
                    onChange={(e) => patchGroup(g, { couleur: e.target.value })}
                  />
                  <input
                    type="text"
                    value={g.nom}
                    className="grpline__nom"
                    onChange={(e) => patchGroup(g, { nom: e.target.value })}
                  />
                  <div className="grpline__compo">
                    {fleetTypes.map((t) => (
                      <label key={t} title={t}>
                        <span>{t}</span>
                        <input
                          type="number"
                          min={0}
                          max={9}
                          value={g.compo[t] || 0}
                          onChange={(e) => patchCompo(g, t, Number(e.target.value) || 0)}
                        />
                      </label>
                    ))}
                  </div>
                  <span className="grpline__n mono">{total(g.compo)} engin(s)</span>
                  <button className="btn btn--sm btn--danger" type="button" onClick={() => deleteGroup(g.id)}>
                    Supprimer
                  </button>
                </div>
              ))
            ) : (
              <div className="empty">Aucun groupe. Créez-en un ci-dessous.</div>
            )}
          </div>

          <p className="bsec">Nouveau groupe</p>
          <div className="grpline grpline--new" style={{ '--c': ngCouleur } as React.CSSProperties}>
            <input type="color" value={ngCouleur} onChange={(e) => setNgCouleur(e.target.value)} />
            <input
              type="text"
              value={ngNom}
              placeholder="Groupe incendie"
              className="grpline__nom"
              onChange={(e) => setNgNom(e.target.value)}
            />
            <div className="grpline__compo">
              {fleetTypes.map((t) => (
                <label key={t} title={t}>
                  <span>{t}</span>
                  <input
                    type="number"
                    min={0}
                    max={9}
                    value={ngCompo[t] || 0}
                    onChange={(e) => setNgCompo((prev) => ({ ...prev, [t]: Number(e.target.value) || 0 }))}
                  />
                </label>
              ))}
            </div>
            <button className="btn btn--sm btn--primary" type="button" onClick={createGroup}>
              Créer le groupe
            </button>
          </div>

          {fleetTypes.length === 0 ? (
            <p className="bwarn" style={{ marginTop: 9 }}>
              ⚠ Aucun type d'engin dans votre parc pour l'instant : les compositions apparaîtront dès
              que vous aurez des véhicules.
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ReglagesDrawer;
